import { parseTaskID } from 'phoenix2/utils/task';
import log from 'loglevel';
import {
    SERVER_TASK_ITEM_STATUS,
    statusMap,
    TASK_ITEM_STATUS,
} from 'phoenix2/state/content/selectors';
import { CLIENT_CONFIG } from 'phoenix2/state/sparxweb/api-config';

function dateToString(d) {
    if (d === null || d === undefined) {
        return '';
    } else if (d.toISOString !== undefined) {
        return d.toISOString();
    } else {
        return d.toString();
    }
}

export const makePackage = pkgData => {
    let taskIds;

    // For diagnostic homework packages, the task ids are provided (as they are not
    // necessarily consecutive, otherwise just generate them based on the number
    // of tasks
    if (pkgData.taskIds && pkgData.taskIds.length > 0) {
        taskIds = pkgData.taskIds;
    } else {
        taskIds = [];
        for (let i = 0; i < pkgData.numTasks; i++) {
            taskIds.push(pkgData.packageID + '/' + (i + 1));
        }
    }

    return {
        active: true,
        end_date: dateToString(pkgData.endDate),
        start_date: dateToString(pkgData.startDate),
        pkg_id: pkgData.packageID,
        hwk_id: pkgData.homeworkID,
        task_ids: taskIds,
        title: pkgData.title,
        package_type: pkgData.packageType,
        generation_status: pkgData.status,
        labels: pkgData.labels,
        deleted_time: dateToString(pkgData.deletedTime),
    };
};

function getPackages(remoteCall, requestGatherer) {
    const req = () =>
        remoteCall('PackageDataRequest', {
            includeAllActivePackages: true,
            getPackages: true,
        });
    return requestGatherer.add(req, 'PKGS');
}

function getTrainingPackages(remoteCall, requestGatherer) {
    const req = () => remoteCall('GetTrainingPackages', {});
    return requestGatherer.add(req, 'TRAINING_PKGS');
}

function getPackage(remoteCall, packageID, requestGatherer) {
    const req = () =>
        remoteCall('PackageDataRequest', {
            getPackages: true,
            packageID: packageID,
        });
    return requestGatherer.add(req, 'PKGS');
}

function loadSizeProgress(completion) {
    const size = completion ? completion.size || 0 : 0;
    const progress = completion ? { ...completion.progress } : {};
    const complete = size > 0 && (progress['C'] || 0) >= size;
    return { size, progress, complete };
}

export function makePackageState(pkgData) {
    return {
        tasksComplete: pkgData.numTasksComplete || 0,
        tasksInProgress: pkgData.numTasksInProgress || 0,
        xPoints: pkgData.xPoints || 0,
        ...loadSizeProgress(pkgData.completion),
    };
}

function getPackageTasks(remoteCall, packageID, requestGatherer) {
    const req = () =>
        remoteCall('PackageDataRequest', {
            packageID: packageID,
            getTasks: true,
            getTaskItems: true,
        });
    return requestGatherer.add(req, 'PKG-' + packageID + '-TASKS');
}

export const makeTaskID = taskData => taskData.packageID + '/' + taskData.taskIndex;

export function makeTask(taskData) {
    return {
        task_id: makeTaskID(taskData),
        items: [],
        package_id: taskData.packageID,
        title: taskData.title,
        delivery_parameters: {
            fixedQuestionDelivery: taskData.fixedQuestionDelivery || false,
            secondChanceDelivery: taskData.secondChanceDelivery || false,
        },
        task_type: taskData.taskType,
        labels: taskData.labels,
    };
}

export const makeTaskItemID = taskItemData =>
    makeTaskID(taskItemData) + '/' + taskItemData.taskItemIndex;

export function makeTaskState(taskData) {
    let completionStore = {};
    if (taskData.completionStore) {
        try {
            completionStore = JSON.parse(taskData.completionStore);
        } catch (e) {
            log.warn('Could not translate task completionStore', taskData);
        }
    }
    return {
        completionStore,
        numItems: taskData.numTaskItems,
        hasBeenComplete: taskData.hasBeenComplete || false,
        ...loadSizeProgress(taskData.completion),
    };
}

function makeTaskItemName(task, taskItemData) {
    switch (task.task_type) {
        case 'INDY_QUIZ':
        case 'PROTOTYPE_ASSESSMENTS':
        default:
            // If the question already has a sensible name set then use that.
            // If not then default to calling it "Item".
            if (/Question [0-9]+/.test(taskItemData.name)) {
                return taskItemData.name;
            } else {
                // There shouldn't be any cases where there is no name and we
                // want to display the item but just in case fallback to the index
                return 'Item ' + (taskItemData.name || taskItemData.taskItemIndex);
            }
    }
}

export function makeTaskItem(task, taskItemData) {
    return {
        id: makeTaskItemID(taskItemData),
        task_id: makeTaskID(taskItemData),
        name: taskItemData.name,
        full_name: makeTaskItemName(task, taskItemData),
        index: taskItemData.taskItemIndex,
        labels: taskItemData.labels,
    };
}

function evaluateEffectiveStatus(tiData, wrongAnswers) {
    // TODO: Deprecate once all servers send SEEKHELP status.
    // Identifies to the client that the version of swworker
    // it is connected to can calculate the seek help status.
    if (tiData.useStatus) {
        return tiData.status;
    }
    if (statusMap[tiData.status] !== TASK_ITEM_STATUS.done) {
        return wrongAnswers >= CLIENT_CONFIG.handsUpTriggers.legacyActive
            ? SERVER_TASK_ITEM_STATUS.SEEKHELP
            : tiData.status;
    }
    return tiData.status;
}

export function makeTaskItemState(tiData) {
    const wrongAnswers = tiData.numWrongAnswers || 0;
    const correctAnswers = tiData.numCorrectAnswers || 0;
    const consecutiveWrong = tiData.numConsecutiveWrongAnswers || 0;
    const effectiveStatus = evaluateEffectiveStatus(tiData, wrongAnswers);
    const res = {
        done: statusMap[tiData.status] === TASK_ITEM_STATUS.done,
        wrongAnswers: wrongAnswers,
        consecutiveWrongAnswers: consecutiveWrong,
        attempts: wrongAnswers + correctAnswers,
        status: effectiveStatus,
        rating: tiData.rating || {},
        numSkillQuestions: tiData.numSkillQuestions,
    };
    return res;
}

export const makeTasksFromPackageData = packageData => {
    const tasks = [];
    const tasksByIndex = {};
    for (const taskData of packageData.tasks || []) {
        const task = makeTask(taskData);
        tasks.push(task);
        tasksByIndex[taskData.taskIndex] = task;
    }
    for (const itemData of packageData.taskItems || []) {
        const task = tasksByIndex[itemData.taskIndex];
        task.items.push(makeTaskItem(task, itemData));
    }
    return tasks;
};

export const getContentAPI = (requestGatherer, bridgeState) => ({
    async getPackages(remoteCall) {
        const [_rType, response] = await getPackages(remoteCall, requestGatherer);
        const packages = (response.packages || []).map(makePackage);
        packages.sort((p1, p2) => (p1.start_date > p2.start_date ? 1 : -1));
        return packages;

        // This can raise InvalidArgument errors or internal server errors which
        // do not need to be handled as they are fatal (just reload the app).
    },

    async getPackage(remoteCall, packageID) {
        const [_rType, response] = await getPackage(remoteCall, packageID, requestGatherer);
        const packages = (response.packages || []).map(makePackage);
        return packages;

        // This can raise InvalidArgument errors or internal server errors which
        // do not need to be handled as they are fatal (just reload the app).
    },

    async getTrainingPackages(remoteCall) {
        const [_rType, response] = await getTrainingPackages(remoteCall, requestGatherer);
        const packages = (response.packages || []).map(makePackage);
        packages.sort((p1, p2) => p1.start_date - p2.start_date);
        return packages;

        // This can raise InvalidArgument errors or internal server errors which
        // do not need to be handled as they are fatal (just reload the app).
    },

    async getPackageStates(remoteCall) {
        const [_rType, response] = await getPackages(remoteCall, requestGatherer);
        const packageStates = {};
        if (response.packages) {
            for (const pkgData of response.packages) {
                packageStates[pkgData.packageID] = makePackageState(pkgData);
            }
        }
        return packageStates;
        // This can raise InvalidArgument errors or internal server errors which
        // do not need to be handled as they are fatal (just reload the app).
        // TODO: NotFound could also be thrown, if a package already being
        // used in the client was deleted. Nothing does that at the moment, but
        // should handle if it becomes possible.
    },

    async getPackageTasks(remoteCall, packageID) {
        const [_rType, response] = await getPackageTasks(remoteCall, packageID, requestGatherer);
        return makeTasksFromPackageData(response);
        // This can raise InvalidArgument errors or internal server errors which
        // do not need to be handled as they are fatal (just reload the app).
        // TODO: NotFound could also be thrown, if a package already being
        // used in the client was deleted. Nothing does that at the moment, but
        // should handle if it becomes possible.
    },

    async getTaskAssets(/*remoteCall, taskID*/) {
        return {
            video: [],
            question: [],
        };
    },

    async getPackageTaskStates(remoteCall, packageID) {
        const [_rType, response] = await getPackageTasks(remoteCall, packageID, requestGatherer);
        const taskStates = {};
        for (const taskData of response.tasks) {
            taskStates[makeTaskID(taskData)] = makeTaskState(taskData);
        }
        return taskStates;
        // This can raise InvalidArgument errors or internal server errors which
        // do not need to be handled as they are fatal (just reload the app).
        // TODO: NotFound could also be thrown, if a task already being
        // used in the client was deleted. Nothing does that at the moment, but
        // should handle if it becomes possible.
    },

    async getTaskItemStates(remoteCall, taskID) {
        const { packageID, taskIndex } = parseTaskID(taskID);
        const [_rType, response] = await remoteCall('PackageDataRequest', {
            packageID,
            taskIndex: parseInt(taskIndex),
            getTaskItems: true,
        });
        const itemStates = {};
        for (const itemData of response.taskItems || []) {
            itemStates[makeTaskItemID(itemData)] = makeTaskItemState(itemData);
        }
        return itemStates;
        // This can raise InvalidArgument errors or internal server errors which
        // do not need to be handled as they are fatal (just reload the app).
        // TODO: NotFound could also be thrown, if a task already being
        // used in the client was deleted. Nothing does that at the moment, but
        // should handle if it becomes possible.
    },

    // NOTE: We still need this as discoveryweb uses it to get a token that it uses
    // to talk to speechserver.
    async getAssetsToken(remoteCall) {
        const [_tp, response] = await remoteCall('AssetsTokenRequest', {});
        return response;
    },
});
